import {
    MultiSelect as PrimereactMultiselect,
} from 'primereact/multiselect';

import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';

import {
    type KeyboardEvent,
    type MouseEvent,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { useTranslation } from 'react-i18next';

// @ts-ignore
import {
    ReactComponent as ArrowDown,
} from '../../assets/images/arrow_down.svg';

// @ts-ignore
import {
    ReactComponent as ValidationErrorIcon,
} from '../../assets/images/validation-error.svg';

// @ts-ignore
import {
    ReactComponent as SearchIcon,
} from '../../assets/images/search-icon.svg';

// @ts-ignore
import {
    ReactComponent as AddIcon,
} from '../../assets/images/dropdown-add-item-icon.svg';

// @ts-ignore
import {
    ReactComponent as DeleteIcon,
} from '../../assets/images/dropdown-delete-item-icon.svg';

// @ts-ignore
import {
    ReactComponent as CloseSmallIcon,
} from '../../assets/images/close_small-icon.svg';

import Checkbox from './Checkbox';

type MultiSelectOption = {
    id: string | number;
    name?: string;
    label?: string;

    [key: string]: any;
};

type ValidatorLike = {
    message: (
        id: string,
        label: any,
        value: MultiSelectOption[],
        validations: any,
    ) => string | null;
};

type MultiSelectProps = {
    showLabel?: boolean;
    label: any;
    small?: boolean;
    searchPlaceholder: any;
    showSelectedCount?: boolean;

    filterFunction: (
        options: MultiSelectOption[],
        search: string,
    ) => MultiSelectOption[];

    validator: ValidatorLike | null;
    validations: any;

    maxSelectedItemsVisible?: number | 'auto';
    selectType?: 'icon' | 'checkbox';
    showSelectAll?: boolean;
    selectAllLabel?: string;

    /**
     * Displays selected options as separate rows
     * underneath the multiselect control.
     */
    selectedItemsBelow?: boolean;

    /**
     * Keeps selected options available inside the
     * dropdown while they are also rendered below.
     */
    duplicateSelectedItems?: boolean;

    /**
     * Controls whether selected values are rendered
     * inside the closed multiselect control.
     */
    showSelectedItemsInControl?: boolean;

    id?: string;
    value?: MultiSelectOption[] | null;
    options?: MultiSelectOption[] | null;
    optionLabel?: string;
    className?: string;
    filter?: boolean;
    placeholder?: string;

    onChange?: (
        event: {
            value: MultiSelectOption[];
        },
    ) => void;

    [key: string]: any;
};

/**
 * API responses sometimes provide numeric IDs while
 * selected values use string IDs. Normalize both sides
 * before comparing them.
 */
const hasSameId = (
    first: string | number,
    second: string | number,
): boolean => String(first) === String(second);

const MultiSelect = ({
    showLabel = true,
    label,
    small = false,
    searchPlaceholder,
    showSelectedCount = false,
    filterFunction,
    validator,
    validations,
    maxSelectedItemsVisible = 1,
    selectType = 'icon',
    showSelectAll = false,
    selectAllLabel = 'common.selectAll',
    selectedItemsBelow = false,
    duplicateSelectedItems = false,
    showSelectedItemsInControl = true,
    ...rest
}: MultiSelectProps): React.ReactElement => {
    const { t } = useTranslation();

    const id = `dropdown-${rest.id}`;
    const optionLabel = rest.optionLabel || 'name';

    const containerRef =
        useRef<HTMLDivElement | null>(null);

    const [search, setSearch] =
        useState('');

    const [maxVisible, setMaxVisible] =
        useState(0);

    const value = useMemo<MultiSelectOption[]>(
        () => (
            Array.isArray(rest.value)
                ? rest.value
                : []
        ),
        [rest.value],
    );

    const baseOptions =
        useMemo<MultiSelectOption[]>(
            () => (
                Array.isArray(rest.options)
                    ? rest.options
                    : []
            ),
            [rest.options],
        );

    /*
     * Preserve selected values that are temporarily
     * missing from the supplied options.
     */
    const options =
        useMemo<MultiSelectOption[]>(
            () => {
                const selectedItemsNotInOptions =
                    value.filter(
                        selectedOption =>
                            !baseOptions.some(
                                option =>
                                    hasSameId(
                                        option.id,
                                        selectedOption.id,
                                    ),
                            ),
                    );

                return [
                    ...baseOptions,
                    ...selectedItemsNotInOptions,
                ];
            },
            [
                baseOptions,
                value,
            ],
        );

    const validationMessage = validator
        ? validator.message(
            id,
            label,
            value,
            validations,
        )
        : null;

    const [
        filteredOptions,
        setFilteredOptions,
    ] = useState<MultiSelectOption[]>(
        options,
    );

    const isCheckbox =
        selectType === 'checkbox';

    useEffect(() => {
        if (filterFunction) {
            setFilteredOptions(
                filterFunction(
                    options,
                    search,
                ),
            );

            return;
        }

        const normalizedSearch =
            search.toLowerCase();

        setFilteredOptions(
            options.filter(option =>
                String(
                    option[optionLabel] ?? '',
                )
                    .toLowerCase()
                    .includes(
                        normalizedSearch,
                    ),
            ),
        );
    }, [
        filterFunction,
        optionLabel,
        options,
        search,
    ]);

    /*
     * When selected values are rendered below the
     * control, they are normally removed from the
     * dropdown.
     *
     * duplicateSelectedItems keeps them in the dropdown
     * so selected items can show the remove icon while
     * unselected ones show the add icon.
     */
    const availableOptions =
        useMemo<MultiSelectOption[]>(
            () => {
                if (
                    isCheckbox ||
                    !selectedItemsBelow ||
                    duplicateSelectedItems
                ) {
                    return filteredOptions;
                }

                return filteredOptions.filter(
                    option =>
                        !value.some(
                            selectedOption =>
                                hasSameId(
                                    selectedOption.id,
                                    option.id,
                                ),
                        ),
                );
            },
            [
                duplicateSelectedItems,
                filteredOptions,
                isCheckbox,
                selectedItemsBelow,
                value,
            ],
        );

    useLayoutEffect(() => {
        if (
            !containerRef.current ||
            !showSelectedItemsInControl
        ) {
            return;
        }

        const temporaryContainer =
            document.createElement('div');

        temporaryContainer.style.visibility =
            'hidden';

        temporaryContainer.style.position =
            'absolute';

        temporaryContainer.style.width =
            'auto';

        temporaryContainer.style.display =
            'flex';

        temporaryContainer.style.flexWrap =
            'nowrap';

        document.body.appendChild(
            temporaryContainer,
        );

        let totalWidth = 0;
        let visibleCount = 0;

        const maximumWidth =
            containerRef.current.offsetWidth -
            40;

        for (
            let index = 0;
            index < value.length;
            index += 1
        ) {
            const selectedItem =
                value[index];

            const span =
                document.createElement('span');

            span.style.display =
                'inline-block';

            span.style.marginRight =
                '8px';

            span.style.padding =
                '16px';

            span.style.borderRadius =
                '6px';

            span.innerText = String(
                selectedItem?.[
                    optionLabel
                ] ?? '',
            );

            temporaryContainer.appendChild(
                span,
            );

            totalWidth +=
                span.offsetWidth + 8;

            if (
                totalWidth <
                maximumWidth
            ) {
                visibleCount += 1;
            } else {
                break;
            }
        }

        document.body.removeChild(
            temporaryContainer,
        );

        setMaxVisible(visibleCount);
    }, [
        maxSelectedItemsVisible,
        optionLabel,
        showSelectedItemsInControl,
        value,
    ]);

    const allFilteredSelected =
        useMemo(
            () => {
                if (
                    !filteredOptions.length
                ) {
                    return false;
                }

                return filteredOptions.every(
                    option =>
                        value.some(
                            selectedOption =>
                                hasSameId(
                                    selectedOption.id,
                                    option.id,
                                ),
                        ),
                );
            },
            [
                filteredOptions,
                value,
            ],
        );

    const scrollHeight = useMemo(
        () => {
            const itemSize = 38;

            const optionsCount =
                availableOptions.length;

            if (optionsCount >= 5) {
                return `${
                    24 + 5 * itemSize
                }px`;
            }

            return `${
                24 +
                optionsCount * itemSize
            }px`;
        },
        [availableOptions],
    );

    const handleChange = (
        nextValue: MultiSelectOption[],
    ): void => {
        rest.onChange?.({
            value: nextValue,
        });
    };

    const toggleOption = (
        option: MultiSelectOption,
    ): void => {
        const selected =
            value.some(
                selectedOption =>
                    hasSameId(
                        selectedOption.id,
                        option.id,
                    ),
            );

        handleChange(
            selected
                ? value.filter(
                    selectedOption =>
                        !hasSameId(
                            selectedOption.id,
                            option.id,
                        ),
                )
                : [
                    ...value,
                    option,
                ],
        );
    };

    const handleOptionClick = (
        event: MouseEvent<Element>,
        option: MultiSelectOption,
    ): void => {
        event.preventDefault();
        event.stopPropagation();

        toggleOption(option);
    };

    const handleOptionKeyDown = (
        event: KeyboardEvent<HTMLElement>,
        option: MultiSelectOption,
    ): void => {
        if (
            event.key !== 'Enter' &&
            event.key !== ' '
        ) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        toggleOption(option);
    };

    const handleRemoveSelectedItem = (
        event: MouseEvent<HTMLButtonElement>,
        item: MultiSelectOption,
    ): void => {
        event.preventDefault();
        event.stopPropagation();

        handleChange(
            value.filter(
                option =>
                    !hasSameId(
                        option.id,
                        item.id,
                    ),
            ),
        );
    };

    const handleSelectAll = (
        checked: boolean,
    ): void => {
        if (checked) {
            const missingOptions =
                filteredOptions.filter(
                    option =>
                        !value.some(
                            selectedOption =>
                                hasSameId(
                                    selectedOption.id,
                                    option.id,
                                ),
                        ),
                );

            handleChange([
                ...value,
                ...missingOptions,
            ]);

            return;
        }

        handleChange(
            value.filter(
                selectedOption =>
                    !filteredOptions.some(
                        option =>
                            hasSameId(
                                option.id,
                                selectedOption.id,
                            ),
                    ),
            ),
        );
    };

    const renderSelectedTokens = (
        visibleItems:
            MultiSelectOption[],
    ): React.ReactElement => (
        <>
            {visibleItems.map(item => (
                <div
                    key={String(item.id)}
                    className={
                        'p-multiselect-token py-1'
                    }
                    style={{
                        lineHeight: '26px',
                    }}
                >
                    <span>
                        {String(
                            item[
                                optionLabel
                            ] ?? '',
                        )}
                    </span>

                    <CloseSmallIcon
                        className={
                            'cursor-pointer'
                        }
                        onClick={(
                            event:
                                MouseEvent<
                                    SVGElement
                                >,
                        ) => {
                            event.preventDefault();
                            event.stopPropagation();

                            handleChange(
                                value.filter(
                                    option =>
                                        !hasSameId(
                                            option.id,
                                            item.id,
                                        ),
                                ),
                            );
                        }}
                    />
                </div>
            ))}
        </>
    );

    const renderSelectedItem = (
        option:
            MultiSelectOption | null,
    ): React.ReactNode => {
        /*
         * Some designs render selected values only below
         * the control. In that case, keep the closed
         * multiselect showing its placeholder.
         */
        if (!showSelectedItemsInControl) {
            const firstSelectedItem =
                value[0];

            const isFirstSelectedItem =
                option &&
                firstSelectedItem &&
                hasSameId(
                    option.id,
                    firstSelectedItem.id,
                );

            if (
                value.length > 0 &&
                !isFirstSelectedItem
            ) {
                return null;
            }

            return (
                <span
                    className={
                        'p-multiselect-label-placeholder'
                    }
                >
                    {rest.placeholder ?? ''}
                </span>
            );
        }

        if (
            selectedItemsBelow &&
            !duplicateSelectedItems
        ) {
            const firstSelectedItem =
                value[0];

            const isFirstSelectedItem =
                option &&
                firstSelectedItem &&
                hasSameId(
                    option.id,
                    firstSelectedItem.id,
                );

            if (!isFirstSelectedItem) {
                return null;
            }

            return (
                <span
                    className={
                        'p-multiselect-label-placeholder'
                    }
                >
                    {rest.placeholder ?? ''}
                </span>
            );
        }

        if (
            value.length > 0 &&
            option &&
            hasSameId(
                value[0].id,
                option.id,
            )
        ) {
            if (
                !options.some(
                    item =>
                        hasSameId(
                            item.id,
                            option.id,
                        ),
                )
            ) {
                return null;
            }

            if (
                maxSelectedItemsVisible ===
                'auto'
            ) {
                const visibleItems =
                    value.slice(
                        0,
                        maxVisible,
                    );

                const hiddenCount =
                    value.length -
                    maxVisible;

                return (
                    <>
                        {renderSelectedTokens(
                            visibleItems,
                        )}

                        {hiddenCount > 0 && (
                            <span
                                className={
                                    'p-multiselect-token py-1'
                                }
                                style={{
                                    lineHeight:
                                        '26px',
                                }}
                            >
                                +{hiddenCount}
                            </span>
                        )}
                    </>
                );
            }

            if (
                typeof
                    maxSelectedItemsVisible ===
                'number'
            ) {
                const visibleItems =
                    value.slice(
                        0,
                        maxSelectedItemsVisible,
                    );

                const hiddenCount =
                    value.length -
                    maxSelectedItemsVisible;

                return (
                    <>
                        {renderSelectedTokens(
                            visibleItems,
                        )}

                        {hiddenCount > 0 && (
                            <span
                                className={
                                    'p-multiselect-token py-1'
                                }
                                style={{
                                    lineHeight:
                                        '26px',
                                }}
                            >
                                +{hiddenCount}
                            </span>
                        )}
                    </>
                );
            }
        }

        if (
            rest.placeholder &&
            value.length === 0
        ) {
            return (
                <span>
                    {rest.placeholder}
                </span>
            );
        }

        return null;
    };

    return (
        <div
            ref={containerRef}
            className={classNames(
                'field',
                rest.className,
            )}
        >
            {showLabel && (
                <div className="flex">
                    <label htmlFor={id}>
                        {label}
                    </label>

                    {showSelectedCount && (
                        <div
                            className={
                                'ml-auto flex ' +
                                'align-items-center mb-1'
                            }
                        >
                            <div
                                className={
                                    'p-multiselect-' +
                                    'selected-count-label'
                                }
                            >
                                {t(
                                    'multiselect.selected',
                                )}
                            </div>

                            <div
                                className={
                                    'p-multiselect-' +
                                    'selected-count-value'
                                }
                            >
                                <div
                                    className={
                                        'p-multiselect-' +
                                        'selected-count-' +
                                        'value-text'
                                    }
                                >
                                    {value.length}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <PrimereactMultiselect
                {...rest}
                id={id}
                className={classNames(
                    'w-full',
                    {
                        small,
                        'p-multiselect-with-checkbox':
                            isCheckbox,
                    },
                )}
                options={availableOptions}
                scrollHeight={scrollHeight}
                dropdownIcon={<ArrowDown />}
                invalid={Boolean(
                    validationMessage,
                )}
                virtualScrollerOptions={{
                    itemSize: 38,
                }}
                display="chip"
                onChange={event => {
                    if (isCheckbox) {
                        handleChange(
                            event.value,
                        );
                    }
                }}
                panelClassName={classNames(
                    'routes-multiselect-panel',
                    {
                        'p-multiselect-item-with-checkbox':
                            isCheckbox,
                    },
                )}
                panelHeaderTemplate={() => (
                    <div
                        className={
                            'p-multiselect-panel-header'
                        }
                    >
                        {showSelectAll && (
                            <>
                                <div className="p-3 pb-3">
                                    <Checkbox
                                        id={
                                            `${id}-select-all`
                                        }
                                        checked={
                                            allFilteredSelected
                                        }
                                        onChange={event =>
                                            handleSelectAll(
                                                event.checked,
                                            )
                                        }
                                        label={t(
                                            selectAllLabel,
                                        )}
                                        showLabel
                                    />
                                </div>

                                <div className="separator" />
                            </>
                        )}

                        {rest.filter && (
                            <div
                                className={classNames(
                                    'p-3',
                                    {
                                        'pt-2':
                                            showSelectAll,
                                    },
                                )}
                            >
                                <span
                                    className={
                                        'p-input-icon-left ' +
                                        'w-full'
                                    }
                                >
                                    <InputText
                                        type="text"
                                        variant="filled"
                                        placeholder={
                                            searchPlaceholder ||
                                            t(
                                                'multiselect.search',
                                            )
                                        }
                                        className={
                                            'small w-full'
                                        }
                                        onChange={event =>
                                            setSearch(
                                                event
                                                    .target
                                                    .value,
                                            )
                                        }
                                        value={search}
                                    />

                                    <SearchIcon />
                                </span>
                            </div>
                        )}
                    </div>
                )}
                itemTemplate={(
                    option:
                        MultiSelectOption,
                ) => {
                    const selected =
                        value.some(
                            selectedOption =>
                                hasSameId(
                                    selectedOption.id,
                                    option.id,
                                ),
                        );

                    if (isCheckbox) {
                        return (
                            <div
                                className={
                                    'p-multiselect-token ' +
                                    'p-multiselect-item-' +
                                    'with-checkbox w-full ' +
                                    'flex justify-content-' +
                                    'between'
                                }
                                style={{
                                    lineHeight:
                                        '26px',
                                }}
                            >
                                <span>
                                    {String(
                                        option[
                                            optionLabel
                                        ] ?? '',
                                    )}
                                </span>
                            </div>
                        );
                    }

                    return (
                        <div
                            className={
                                'p-multiselect-token ' +
                                'py-1 w-full flex ' +
                                'justify-content-between ' +
                                'cursor-pointer'
                            }
                            style={{
                                lineHeight: '26px',
                            }}
                            role="button"
                            tabIndex={0}
                            onClick={event =>
                                handleOptionClick(
                                    event,
                                    option,
                                )
                            }
                            onKeyDown={event =>
                                handleOptionKeyDown(
                                    event,
                                    option,
                                )
                            }
                        >
                            <span>
                                {String(
                                    option[
                                        optionLabel
                                    ] ?? '',
                                )}
                            </span>

                            {selected ? (
                                <DeleteIcon
                                    className={
                                        'cursor-pointer'
                                    }
                                    onClick={event =>
                                        handleOptionClick(
                                            event,
                                            option,
                                        )
                                    }
                                />
                            ) : (
                                <AddIcon
                                    className={
                                        'cursor-pointer'
                                    }
                                    onClick={event =>
                                        handleOptionClick(
                                            event,
                                            option,
                                        )
                                    }
                                />
                            )}
                        </div>
                    );
                }}
                selectedItemTemplate={
                    renderSelectedItem
                }
            />

            {selectedItemsBelow &&
                value.length > 0 && (
                    <div
                        className={
                            'p-multiselect-selected-items'
                        }
                    >
                        {value.map(item => (
                            <div
                                key={String(
                                    item.id,
                                )}
                                className={
                                    'p-multiselect-' +
                                    'selected-item'
                                }
                            >
                                <span
                                    className={
                                        'p-multiselect-' +
                                        'selected-item__name'
                                    }
                                    title={String(
                                        item[
                                            optionLabel
                                        ] ?? '',
                                    )}
                                >
                                    {String(
                                        item[
                                            optionLabel
                                        ] ?? '',
                                    )}
                                </span>

                                <button
                                    type="button"
                                    className={
                                        'p-multiselect-' +
                                        'selected-item__remove'
                                    }
                                    aria-label="Remove"
                                    onClick={event =>
                                        handleRemoveSelectedItem(
                                            event,
                                            item,
                                        )
                                    }
                                >
                                    <span
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

            {validationMessage && (
                <div className="validation-error">
                    <ValidationErrorIcon
                        className="icon"
                    />

                    <div className="text">
                        {validationMessage}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiSelect;